import React, { useState } from "react";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../components/panel/panel.jsx";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import PackageEntitlementsEditor, {
  emptyEntitlement,
  sumEntitlementPrices,
  toApiEntitlements,
} from "./packageEntitlementsEditor.jsx";
import "./../compnay/AddCompanyStyles.css";

function AddPackage() {
  const navigate = useNavigate();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [loading, setLoading] = useState(false);
  const [entitlements, setEntitlements] = useState([emptyEntitlement(0)]);

  const [packageData, setPackageData] = useState({
    pName: "",
    pDetail: "",
    pStatus: true,
    pVatPercent: 20,
  });

  const inputStyle = {
    borderRadius: "10px",
    border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
    background: isDarkMode ? "#2d3436" : "#F9F9F9",
    color: isDarkMode ? "#ffffff" : "#333333",
    height: "44px",
  };

  const labelColor = { color: isDarkMode ? "#e0e0e0" : "#495057" };
  const mutedColor = { color: isDarkMode ? "#adb5bd" : "#6c757d" };

  const handleInputChange = (e, field) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setPackageData({ ...packageData, [field]: value });
  };

  const handleSubmit = async () => {
    const { pName } = packageData;
    const vatPercent = parseFloat(packageData.pVatPercent);
    const total = sumEntitlementPrices(entitlements);
    const apiEntitlements = toApiEntitlements(entitlements);

    if (!pName) {
      showErrorToast("Please fill in package name.");
      return;
    }

    if (apiEntitlements.length === 0) {
      showErrorToast("Add at least one entitlement.");
      return;
    }

    if (total <= 0) {
      showErrorToast("Package total (sum of entitlement prices) must be greater than 0.");
      return;
    }

    if (Number.isNaN(vatPercent) || vatPercent < 0 || vatPercent > 100) {
      showErrorToast("VAT percent must be between 0 and 100.");
      return;
    }

    try {
      setLoading(true);

      const packageResponse = await axiosInstance.post(APIs.POST_ADD_PACKAGE, {
        PName: packageData.pName,
        PDetail: packageData.pDetail,
        PVatPercent: vatPercent,
        PStatus: packageData.pStatus,
        Entitlements: apiEntitlements,
      });

      if (!packageResponse.data.status) {
        showErrorToast(packageResponse.data.message || "Failed to create package");
        return;
      }

      showSuccessToast("Package created successfully");
      navigate("/packages/managePackages");
    } catch (error) {
      console.error(error);
      showErrorToast("Failed to create package");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-2 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/packages/managePackages">Manage Packages</Link>
            </li>
            <li className="breadcrumb-item active">Add Package</li>
          </ol>
          <h1 className="page-header mb-0">Add New Package</h1>
        </div>
        <div className="ms-auto">
          <button
            onClick={handleSubmit}
            className="btn px-4"
            disabled={loading}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Create Package
          </button>
        </div>
      </div>

      <Panel>
        <PanelHeader>Package Details</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-12">
              <FormInput
                label="Package Name"
                placeHolder="Enter Package Name"
                type="text"
                value={packageData.pName}
                onchange={(e) => handleInputChange(e, "pName")}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <label style={labelColor}>Description</label>
              <textarea
                className="form-control"
                placeholder="Enter package description"
                value={packageData.pDetail}
                onChange={(e) => handleInputChange(e, "pDetail")}
                rows="3"
                style={{
                  borderRadius: "10px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#ffffff" : "#333333",
                }}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-4">
              <label style={labelColor}>VAT (%)</label>
              <input
                type="number"
                className="form-control"
                placeholder="20"
                value={packageData.pVatPercent}
                onChange={(e) => handleInputChange(e, "pVatPercent")}
                step="0.01"
                min="0"
                max="100"
                style={inputStyle}
              />
              <small style={mutedColor}>Applied to package total on invoices</small>
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="pStatus"
                  checked={packageData.pStatus}
                  onChange={(e) => handleInputChange(e, "pStatus")}
                />
                <label className="form-check-label" htmlFor="pStatus" style={labelColor}>
                  Active
                </label>
              </div>
            </div>
          </div>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader>Includes (priced entitlements)</PanelHeader>
        <PanelBody>
          <PackageEntitlementsEditor
            entitlements={entitlements}
            onChange={setEntitlements}
            inputStyle={inputStyle}
            labelColor={labelColor}
            mutedColor={mutedColor}
            themeColor={themeColor}
            isDarkMode={isDarkMode}
          />
        </PanelBody>
      </Panel>
    </div>
  );
}

export default AddPackage;
